import dayjs from 'dayjs';
import {
  FluxoCaixaCategoria,
  FluxoCaixaCategoriaLinha,
  FluxoCaixaEvolucaoMensal,
  FluxoCaixaLancamento,
  FluxoCaixaResumo,
  FluxoCaixaTipo,
} from '../types/fluxoCaixa';

export function calcularResumoDeLancamentos(
  lancamentos: FluxoCaixaLancamento[]
): FluxoCaixaResumo {
  const resumo = lancamentos.reduce(
    (acc, lancamento) => {
      if (lancamento.lancamento_tipo === FluxoCaixaTipo.ENTRADA) {
        acc.total_entrada += lancamento.valor;
      } else {
        acc.total_saida += lancamento.valor;
      }
      return acc;
    },
    { total_entrada: 0, total_saida: 0, saldo: 0 }
  );
  resumo.saldo = resumo.total_entrada - resumo.total_saida;
  return resumo;
}

export function agruparPorCategoria(
  lancamentos: FluxoCaixaLancamento[]
): FluxoCaixaCategoriaLinha[] {
  const map = new Map<FluxoCaixaCategoria, { entrada: number; saida: number }>();

  for (const lancamento of lancamentos) {
    const atual = map.get(lancamento.categoria) ?? { entrada: 0, saida: 0 };
    if (lancamento.lancamento_tipo === FluxoCaixaTipo.ENTRADA) {
      atual.entrada += lancamento.valor;
    } else {
      atual.saida += lancamento.valor;
    }
    map.set(lancamento.categoria, atual);
  }

  const totalMovimentado = [...map.values()].reduce(
    (acc, item) => acc + item.entrada + item.saida,
    0
  );

  return [...map.entries()]
    .map(([categoria, valores]) => ({
      categoria,
      entrada: valores.entrada,
      saida: valores.saida,
      liquido: valores.entrada - valores.saida,
      percentual:
        totalMovimentado > 0
          ? ((valores.entrada + valores.saida) / totalMovimentado) * 100
          : 0,
    }))
    .sort((a, b) => b.entrada + b.saida - (a.entrada + a.saida));
}

export function agruparPorMes(
  lancamentos: FluxoCaixaLancamento[],
  meses = 3
): FluxoCaixaEvolucaoMensal[] {
  const hoje = dayjs();
  const resultado: FluxoCaixaEvolucaoMensal[] = [];

  for (let i = meses - 1; i >= 0; i--) {
    const ref = hoje.subtract(i, 'month');
    const mesKey = ref.format('YYYY-MM');
    resultado.push({
      mes: mesKey,
      label: ref.format('MMM/YY'),
      entrada: 0,
      saida: 0,
      saldo: 0,
    });
  }

  const indexPorMes = new Map(resultado.map((item, index) => [item.mes, index]));

  for (const lancamento of lancamentos) {
    const mesKey = dayjs(lancamento.data_lancamento).format('YYYY-MM');
    const index = indexPorMes.get(mesKey);
    if (index === undefined) continue;

    if (lancamento.lancamento_tipo === FluxoCaixaTipo.ENTRADA) {
      resultado[index].entrada += lancamento.valor;
    } else {
      resultado[index].saida += lancamento.valor;
    }
  }

  return resultado.map(item => ({
    ...item,
    saldo: item.entrada - item.saida,
  }));
}
